/**
 * The pure before/after diff logic behind `AuditEntryDetail` (spec D-6), in
 * its own module rather than the component file: `react-refresh/only-export-
 * components` requires a file that exports a component to export nothing but
 * components and types (`Select.tsx` sets the same precedent), and
 * `AuditEntryTable` needs `hasDiff` too — per entry in a grouped row
 * (decision V-8), to decide whether that entry's toggle/panel should exist at
 * all (review round 1, M-1).
 *
 * **Only fields that DIFFER are shown.** A case update that touches one
 * field out of forty must render one row, not forty. Two states are compared
 * key by key over the UNION of both objects' keys (a key present in one and
 * absent in the other is itself a difference — an added or a removed field,
 * distinct from a changed value), and a value "differs" when
 * `JSON.stringify` of each side differs. That is sufficient for the
 * plain-JSON shapes `AuditEntryResource` actually emits — strings, numbers,
 * booleans, `null`, and nested plain objects/arrays — and is not a general
 * deep-equality routine, so it does not need to worry about key order inside
 * a nested object changing the string (the API does not reorder object keys
 * between a before/after pair of the same record).
 *
 * A `null` side is coerced to `{}` before diffing (M-2, review round 1) — the
 * two commonest audit rows in production carry ONE null side: a create
 * (`before_state: null`) or a delete (`after_state: null`). Coercing to `{}`
 * means every field on the populated side reads as "Added" or "Removed"
 * rather than as forty unrenderable rows with a blank column.
 *
 * A recorded state that was too large to store arrives as the single-key
 * sentinel `{"_bounds": "..."}` (`AuditLogger::state()`, API repo). That is
 * not a field named `_bounds` a user changed — it is the API telling this
 * view it dropped the real state (M-5, review round 1); `hasDiff` treats it
 * as "there is something to show" and `AuditEntryDetail` renders a notice
 * rather than a diffed field for it.
 */

export interface FieldDiff {
  key: string;
  hadBefore: boolean;
  hadAfter: boolean;
  before: unknown;
  after: unknown;
}

const BOUNDS_KEY = '_bounds';

/** `AuditLogger::state()`'s "the real state did not fit" sentinel: a state
 *  object reduced to exactly one key, `_bounds`, holding a string reason. */
export function isTruncated(state: Record<string, unknown> | null): boolean {
  if (state === null) return false;
  const keys = Object.keys(state);
  return keys.length === 1 && keys[0] === BOUNDS_KEY && typeof state[BOUNDS_KEY] === 'string';
}

export function diffFields(
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
): readonly FieldDiff[] {
  if (before === null && after === null) return [];

  const beforeObj = before ?? {};
  const afterObj = after ?? {};
  // Insertion order: every `before` key first, then any `after`-only key —
  // deterministic for a fixed pair of objects, which is all a diff needs.
  const keys = new Set([...Object.keys(beforeObj), ...Object.keys(afterObj)]);

  const diffs: FieldDiff[] = [];
  for (const key of keys) {
    const hadBefore = Object.prototype.hasOwnProperty.call(beforeObj, key);
    const hadAfter = Object.prototype.hasOwnProperty.call(afterObj, key);
    const beforeValue = beforeObj[key];
    const afterValue = afterObj[key];

    if (JSON.stringify(beforeValue) === JSON.stringify(afterValue)) continue;

    diffs.push({ key, hadBefore, hadAfter, before: beforeValue, after: afterValue });
  }
  return diffs;
}

/**
 * Whether `AuditEntryDetail` would render anything at all for this pair —
 * a truncation notice (either side is the `_bounds` sentinel) or a non-empty
 * diff. `AuditEntryTable` uses this, not "either side is non-null", to
 * decide whether an entry's toggle/panel should exist: a recorded-but-
 * identical pair has non-null state and still has nothing to show (ruling,
 * review round 1).
 */
export function hasDiff(
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
): boolean {
  if (isTruncated(before) || isTruncated(after)) return true;
  return diffFields(before, after).length > 0;
}
